import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Check, Building2, User, Settings, Search, Loader2, Rocket, CheckCircle2, XCircle, ShieldCheck, Database, Table2, SlidersHorizontal, Factory, UserCog, KeyRound, Hash, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import onboardingApi from '../utils/onboardingApi';
import useAuthStore from '../store/authStore';

const TIPO_IDENTIFICACION = [
  { value: '01', label: 'Cédula Física', length: 9, format: 'X-XXXX-XXXX', placeholder: '1-2345-6789' },
  { value: '02', label: 'Cédula Jurídica', length: 10, format: 'X-XXX-XXXXXX', placeholder: '3-102-123456' },
  { value: '03', label: 'DIMEX', length: 12, format: 'XXXXXXXXXXXX', placeholder: '123456789012' },
  { value: '04', label: 'NITE', length: 10, format: 'XXXXXXXXXX', placeholder: '1234567890' }
];

const PROVINCIAS = [
  { value: '1', label: 'San José' },
  { value: '2', label: 'Alajuela' },
  { value: '3', label: 'Cartago' },
  { value: '4', label: 'Heredia' },
  { value: '5', label: 'Guanacaste' },
  { value: '6', label: 'Puntarenas' },
  { value: '7', label: 'Limón' }
];

// Cantones por provincia (código Hacienda)
const CANTONES = {
  '1': [ // San José
    { value: '01', label: 'San José' }, { value: '02', label: 'Escazú' }, { value: '03', label: 'Desamparados' },
    { value: '04', label: 'Puriscal' }, { value: '05', label: 'Tarrazú' }, { value: '06', label: 'Aserrí' },
    { value: '07', label: 'Mora' }, { value: '08', label: 'Goicoechea' }, { value: '09', label: 'Santa Ana' },
    { value: '10', label: 'Alajuelita' }, { value: '11', label: 'Vázquez de Coronado' }, { value: '12', label: 'Acosta' },
    { value: '13', label: 'Tibás' }, { value: '14', label: 'Moravia' }, { value: '15', label: 'Montes de Oca' },
    { value: '16', label: 'Turrubares' }, { value: '17', label: 'Dota' }, { value: '18', label: 'Curridabat' },
    { value: '19', label: 'Pérez Zeledón' }, { value: '20', label: 'León Cortés Castro' }
  ],
  '2': [ // Alajuela
    { value: '01', label: 'Alajuela' }, { value: '02', label: 'San Ramón' }, { value: '03', label: 'Grecia' },
    { value: '04', label: 'San Mateo' }, { value: '05', label: 'Atenas' }, { value: '06', label: 'Naranjo' },
    { value: '07', label: 'Palmares' }, { value: '08', label: 'Poás' }, { value: '09', label: 'Orotina' },
    { value: '10', label: 'San Carlos' }, { value: '11', label: 'Zarcero' }, { value: '12', label: 'Sarchí' },
    { value: '13', label: 'Upala' }, { value: '14', label: 'Los Chiles' }, { value: '15', label: 'Guatuso' },
    { value: '16', label: 'Río Cuarto' }
  ],
  '3': [ // Cartago
    { value: '01', label: 'Cartago' }, { value: '02', label: 'Paraíso' }, { value: '03', label: 'La Unión' },
    { value: '04', label: 'Jiménez' }, { value: '05', label: 'Turrialba' }, { value: '06', label: 'Alvarado' },
    { value: '07', label: 'Oreamuno' }, { value: '08', label: 'El Guarco' }
  ],
  '4': [ // Heredia
    { value: '01', label: 'Heredia' }, { value: '02', label: 'Barva' }, { value: '03', label: 'Santo Domingo' },
    { value: '04', label: 'Santa Bárbara' }, { value: '05', label: 'San Rafael' }, { value: '06', label: 'San Isidro' },
    { value: '07', label: 'Belén' }, { value: '08', label: 'Flores' }, { value: '09', label: 'San Pablo' },
    { value: '10', label: 'Sarapiquí' }
  ],
  '5': [ // Guanacaste
    { value: '01', label: 'Liberia' }, { value: '02', label: 'Nicoya' }, { value: '03', label: 'Santa Cruz' },
    { value: '04', label: 'Bagaces' }, { value: '05', label: 'Carrillo' }, { value: '06', label: 'Cañas' },
    { value: '07', label: 'Abangares' }, { value: '08', label: 'Tilarán' }, { value: '09', label: 'Nandayure' },
    { value: '10', label: 'La Cruz' }, { value: '11', label: 'Hojancha' }
  ],
  '6': [ // Puntarenas
    { value: '01', label: 'Puntarenas' }, { value: '02', label: 'Esparza' }, { value: '03', label: 'Buenos Aires' },
    { value: '04', label: 'Montes de Oro' }, { value: '05', label: 'Osa' }, { value: '06', label: 'Quepos' },
    { value: '07', label: 'Golfito' }, { value: '08', label: 'Coto Brus' }, { value: '09', label: 'Parrita' },
    { value: '10', label: 'Corredores' }, { value: '11', label: 'Garabito' }, { value: '12', label: 'Monte Verde' },
    { value: '13', label: 'Puerto Jiménez' }
  ],
  '7': [ // Limón
    { value: '01', label: 'Limón' }, { value: '02', label: 'Pococí' }, { value: '03', label: 'Siquirres' },
    { value: '04', label: 'Talamanca' }, { value: '05', label: 'Matina' }, { value: '06', label: 'Guácimo' }
  ]
};

// Distritos por provincia-canton (código Hacienda)
const DISTRITOS = {
  '1-01': [ // San José - San José
    { value: '01', label: 'Carmen' }, { value: '02', label: 'Merced' }, { value: '03', label: 'Hospital' },
    { value: '04', label: 'Catedral' }, { value: '05', label: 'Zapote' }, { value: '06', label: 'San Francisco de Dos Ríos' },
    { value: '07', label: 'Uruca' }, { value: '08', label: 'Mata Redonda' }, { value: '09', label: 'Pavas' },
    { value: '10', label: 'Hatillo' }, { value: '11', label: 'San Sebastián' }
  ],
  '1-02': [ // San José - Escazú
    { value: '01', label: 'Escazú' }, { value: '02', label: 'San Antonio' }, { value: '03', label: 'San Rafael' }
  ],
  '1-03': [ // San José - Desamparados
    { value: '01', label: 'Desamparados' }, { value: '02', label: 'San Miguel' }, { value: '03', label: 'San Juan de Dios' },
    { value: '04', label: 'San Rafael Arriba' }, { value: '05', label: 'San Antonio' }, { value: '06', label: 'Frailes' },
    { value: '07', label: 'Patarrá' }, { value: '08', label: 'San Cristóbal' }, { value: '09', label: 'Rosario' },
    { value: '10', label: 'Damas' }, { value: '11', label: 'San Rafael Abajo' }, { value: '12', label: 'Gravilias' },
    { value: '13', label: 'Los Guido' }
  ],
  '1-04': [ // San José - Puriscal
    { value: '01', label: 'Santiago' }, { value: '02', label: 'Mercedes Sur' }, { value: '03', label: 'Barbacoas' },
    { value: '04', label: 'Grifo Alto' }, { value: '05', label: 'San Rafael' }, { value: '06', label: 'Candelarita' },
    { value: '07', label: 'Desamparaditos' }, { value: '08', label: 'San Antonio' }, { value: '09', label: 'Chires' }
  ],
  '1-05': [ // San José - Tarrazú
    { value: '01', label: 'San Marcos' }, { value: '02', label: 'San Lorenzo' }, { value: '03', label: 'San Carlos' }
  ],
  '1-06': [ // San José - Aserrí
    { value: '01', label: 'Aserrí' }, { value: '02', label: 'Tarbaca' }, { value: '03', label: 'Vuelta de Jorco' },
    { value: '04', label: 'San Gabriel' }, { value: '05', label: 'Legua' }, { value: '06', label: 'Monterrey' },
    { value: '07', label: 'Salitrillos' }
  ],
  '1-07': [ // San José - Mora
    { value: '01', label: 'Colón' }, { value: '02', label: 'Guayabo' }, { value: '03', label: 'Tabarcia' },
    { value: '04', label: 'Piedras Negras' }, { value: '05', label: 'Picagres' }, { value: '06', label: 'Jaris' },
    { value: '07', label: 'Quitirrisí' }
  ],
  '1-08': [ // San José - Goicoechea
    { value: '01', label: 'Guadalupe' }, { value: '02', label: 'San Francisco' }, { value: '03', label: 'Calle Blancos' },
    { value: '04', label: 'Mata de Plátano' }, { value: '05', label: 'Ipís' }, { value: '06', label: 'Rancho Redondo' },
    { value: '07', label: 'Purral' }
  ],
  '1-09': [ // San José - Santa Ana
    { value: '01', label: 'Santa Ana' }, { value: '02', label: 'Salitral' }, { value: '03', label: 'Pozos' },
    { value: '04', label: 'Uruca' }, { value: '05', label: 'Piedades' }, { value: '06', label: 'Brasil' }
  ],
  '1-10': [ // San José - Alajuelita
    { value: '01', label: 'Alajuelita' }, { value: '02', label: 'San Josecito' }, { value: '03', label: 'San Antonio' },
    { value: '04', label: 'Concepción' }, { value: '05', label: 'San Felipe' }
  ],
  '1-11': [ // San José - Vázquez de Coronado
    { value: '01', label: 'San Isidro' }, { value: '02', label: 'San Rafael' }, { value: '03', label: 'Dulce Nombre de Jesús' },
    { value: '04', label: 'Patalillo' }, { value: '05', label: 'Cascajal' }
  ],
  '1-12': [ // San José - Acosta
    { value: '01', label: 'San Ignacio' }, { value: '02', label: 'Guaitil' }, { value: '03', label: 'Palmichal' },
    { value: '04', label: 'Cangrejal' }, { value: '05', label: 'Sabanillas' }
  ],
  '1-13': [ // San José - Tibás
    { value: '01', label: 'San Juan' }, { value: '02', label: 'Cinco Esquinas' }, { value: '03', label: 'Anselmo Llorente' },
    { value: '04', label: 'León XIII' }, { value: '05', label: 'Colima' }
  ],
  '1-14': [ // San José - Moravia
    { value: '01', label: 'San Vicente' }, { value: '02', label: 'San Jerónimo' }, { value: '03', label: 'La Trinidad' }
  ],
  '1-15': [ // San José - Montes de Oca
    { value: '01', label: 'San Pedro' }, { value: '02', label: 'Sabanilla' }, { value: '03', label: 'Mercedes' },
    { value: '04', label: 'San Rafael' }
  ],
  '1-16': [ // San José - Turrubares
    { value: '01', label: 'San Pablo' }, { value: '02', label: 'San Pedro' }, { value: '03', label: 'San Juan de Mata' },
    { value: '04', label: 'San Luis' }, { value: '05', label: 'Carara' }
  ],
  '1-17': [ // San José - Dota
    { value: '01', label: 'Santa María' }, { value: '02', label: 'Jardín' }, { value: '03', label: 'Copey' }
  ],
  '1-18': [ // San José - Curridabat
    { value: '01', label: 'Curridabat' }, { value: '02', label: 'Granadilla' }, { value: '03', label: 'Sánchez' },
    { value: '04', label: 'Tirrases' }
  ],
  '1-19': [ // San José - Pérez Zeledón
    { value: '01', label: 'San Isidro de El General' }, { value: '02', label: 'El General' }, { value: '03', label: 'Daniel Flores' },
    { value: '04', label: 'Rivas' }, { value: '05', label: 'San Pedro' }, { value: '06', label: 'Platanares' },
    { value: '07', label: 'Pejibaye' }, { value: '08', label: 'Cajón' }, { value: '09', label: 'Barú' },
    { value: '10', label: 'Río Nuevo' }, { value: '11', label: 'Páramo' }, { value: '12', label: 'La Amistad' }
  ],
  '1-20': [ // San José - León Cortés Castro
    { value: '01', label: 'San Pablo' }, { value: '02', label: 'San Andrés' }, { value: '03', label: 'Llano Bonito' },
    { value: '04', label: 'San Isidro' }, { value: '05', label: 'Santa Cruz' }, { value: '06', label: 'San Antonio' }
  ],
  '2-01': [ // Alajuela - Alajuela
    { value: '01', label: 'Alajuela' }, { value: '02', label: 'San José' }, { value: '03', label: 'Carrizal' },
    { value: '04', label: 'San Antonio' }, { value: '05', label: 'Guácima' }, { value: '06', label: 'San Isidro' },
    { value: '07', label: 'Sabanilla' }, { value: '08', label: 'San Rafael' }, { value: '09', label: 'Río Segundo' },
    { value: '10', label: 'Desamparados' }, { value: '11', label: 'Turrúcares' }, { value: '12', label: 'Tambor' },
    { value: '13', label: 'Garita' }, { value: '14', label: 'Sarapiquí' }
  ],
  '2-02': [ // Alajuela - San Ramón
    { value: '01', label: 'San Ramón' }, { value: '02', label: 'Santiago' }, { value: '03', label: 'San Juan' },
    { value: '04', label: 'Piedades Norte' }, { value: '05', label: 'Piedades Sur' }, { value: '06', label: 'San Rafael' },
    { value: '07', label: 'San Isidro' }, { value: '08', label: 'Ángeles' }, { value: '09', label: 'Alfaro' },
    { value: '10', label: 'Volio' }, { value: '11', label: 'Concepción' }, { value: '12', label: 'Zapotal' },
    { value: '13', label: 'Peñas Blancas' }, { value: '14', label: 'San Lorenzo' }
  ],
  '2-03': [ // Alajuela - Grecia
    { value: '01', label: 'Grecia' }, { value: '02', label: 'San Isidro' }, { value: '03', label: 'San José' },
    { value: '04', label: 'San Roque' }, { value: '05', label: 'Tacares' }, { value: '06', label: 'Puente de Piedra' },
    { value: '07', label: 'Bolívar' }
  ],
  '2-04': [ // Alajuela - San Mateo
    { value: '01', label: 'San Mateo' }, { value: '02', label: 'Desmonte' }, { value: '03', label: 'Jesús María' },
    { value: '04', label: 'Labrador' }
  ],
  '2-05': [ // Alajuela - Atenas
    { value: '01', label: 'Atenas' }, { value: '02', label: 'Jesús' }, { value: '03', label: 'Mercedes' },
    { value: '04', label: 'San Isidro' }, { value: '05', label: 'Concepción' }, { value: '06', label: 'San José' },
    { value: '07', label: 'Santa Eulalia' }, { value: '08', label: 'Escobal' }
  ],
  '2-06': [ // Alajuela - Naranjo
    { value: '01', label: 'Naranjo' }, { value: '02', label: 'San Miguel' }, { value: '03', label: 'San José' },
    { value: '04', label: 'Cirrí Sur' }, { value: '05', label: 'San Jerónimo' }, { value: '06', label: 'San Juan' },
    { value: '07', label: 'El Rosario' }, { value: '08', label: 'Palmitos' }
  ],
  '2-07': [ // Alajuela - Palmares
    { value: '01', label: 'Palmares' }, { value: '02', label: 'Zaragoza' }, { value: '03', label: 'Buenos Aires' },
    { value: '04', label: 'Santiago' }, { value: '05', label: 'Candelaria' }, { value: '06', label: 'Esquipulas' },
    { value: '07', label: 'La Granja' }
  ],
  '2-08': [ // Alajuela - Poás
    { value: '01', label: 'San Pedro' }, { value: '02', label: 'San Juan' }, { value: '03', label: 'San Rafael' },
    { value: '04', label: 'Carrillos' }, { value: '05', label: 'Sabana Redonda' }
  ],
  '2-09': [ // Alajuela - Orotina
    { value: '01', label: 'Orotina' }, { value: '02', label: 'El Mastate' }, { value: '03', label: 'Hacienda Vieja' },
    { value: '04', label: 'Coyolar' }, { value: '05', label: 'La Ceiba' }
  ],
  '2-10': [ // Alajuela - San Carlos
    { value: '01', label: 'Quesada' }, { value: '02', label: 'Florencia' }, { value: '03', label: 'Buenavista' },
    { value: '04', label: 'Aguas Zarcas' }, { value: '05', label: 'Venecia' }, { value: '06', label: 'Pital' },
    { value: '07', label: 'La Fortuna' }, { value: '08', label: 'La Tigra' }, { value: '09', label: 'La Palmera' },
    { value: '10', label: 'Venado' }, { value: '11', label: 'Cutris' }, { value: '12', label: 'Monterrey' },
    { value: '13', label: 'Pocosol' }
  ],
  '2-11': [ // Alajuela - Zarcero
    { value: '01', label: 'Zarcero' }, { value: '02', label: 'Laguna' }, { value: '03', label: 'Tapesco' },
    { value: '04', label: 'Guadalupe' }, { value: '05', label: 'Palmira' }, { value: '06', label: 'Zapote' },
    { value: '07', label: 'Brisas' }
  ],
  '2-12': [ // Alajuela - Sarchí (Valverde Vega)
    { value: '01', label: 'Sarchí Norte' }, { value: '02', label: 'Sarchí Sur' }, { value: '03', label: 'Toro Amarillo' },
    { value: '04', label: 'San Pedro' }, { value: '05', label: 'Rodríguez' }
  ],
  '2-13': [ // Alajuela - Upala
    { value: '01', label: 'Upala' }, { value: '02', label: 'Aguas Claras' }, { value: '03', label: 'San José' },
    { value: '04', label: 'Bijagua' }, { value: '05', label: 'Delicias' }, { value: '06', label: 'Dos Ríos' },
    { value: '07', label: 'Yolillal' }, { value: '08', label: 'Canalete' }
  ],
  '2-14': [ // Alajuela - Los Chiles
    { value: '01', label: 'Los Chiles' }, { value: '02', label: 'Caño Negro' }, { value: '03', label: 'El Amparo' },
    { value: '04', label: 'San Jorge' }
  ],
  '2-15': [ // Alajuela - Guatuso
    { value: '01', label: 'San Rafael' }, { value: '02', label: 'Buenavista' }, { value: '03', label: 'Cote' },
    { value: '04', label: 'Katira' }
  ],
  '2-16': [ // Alajuela - Río Cuarto
    { value: '01', label: 'Río Cuarto' }, { value: '02', label: 'Santa Rita' }, { value: '03', label: 'Santa Isabel' }
  ],
  '3-01': [ // Cartago - Cartago
    { value: '01', label: 'Oriental' }, { value: '02', label: 'Occidental' }, { value: '03', label: 'Carmen' },
    { value: '04', label: 'San Nicolás' }, { value: '05', label: 'Aguacaliente' }, { value: '06', label: 'Guadalupe' },
    { value: '07', label: 'Corralillo' }, { value: '08', label: 'Tierra Blanca' }, { value: '09', label: 'Dulce Nombre' },
    { value: '10', label: 'Llano Grande' }, { value: '11', label: 'Quebradilla' }
  ],
  '3-02': [ // Cartago - Paraíso
    { value: '01', label: 'Paraíso' }, { value: '02', label: 'Santiago' }, { value: '03', label: 'Orosi' },
    { value: '04', label: 'Cachí' }, { value: '05', label: 'Llanos de Santa Lucía' }
  ],
  '3-03': [ // Cartago - La Unión
    { value: '01', label: 'Tres Ríos' }, { value: '02', label: 'San Diego' }, { value: '03', label: 'San Juan' },
    { value: '04', label: 'San Rafael' }, { value: '05', label: 'Concepción' }, { value: '06', label: 'Dulce Nombre' },
    { value: '07', label: 'San Ramón' }, { value: '08', label: 'Río Azul' }
  ],
  '3-04': [ // Cartago - Jiménez
    { value: '01', label: 'Juan Viñas' }, { value: '02', label: 'Tucurrique' }, { value: '03', label: 'Pejibaye' }
  ],
  '3-05': [ // Cartago - Turrialba
    { value: '01', label: 'Turrialba' }, { value: '02', label: 'La Suiza' }, { value: '03', label: 'Peralta' },
    { value: '04', label: 'Santa Cruz' }, { value: '05', label: 'Santa Teresita' }, { value: '06', label: 'Pavones' },
    { value: '07', label: 'Tuis' }, { value: '08', label: 'Tayutic' }, { value: '09', label: 'Santa Rosa' },
    { value: '10', label: 'Tres Equis' }, { value: '11', label: 'La Isabel' }, { value: '12', label: 'Chirripó' }
  ],
  '3-06': [ // Cartago - Alvarado
    { value: '01', label: 'Pacayas' }, { value: '02', label: 'Cervantes' }, { value: '03', label: 'Capellades' }
  ],
  '3-07': [ // Cartago - Oreamuno
    { value: '01', label: 'San Rafael' }, { value: '02', label: 'Cot' }, { value: '03', label: 'Potrero Cerrado' },
    { value: '04', label: 'Cipreses' }, { value: '05', label: 'Santa Rosa' }
  ],
  '3-08': [ // Cartago - El Guarco
    { value: '01', label: 'El Tejar' }, { value: '02', label: 'San Isidro' }, { value: '03', label: 'Tobosi' },
    { value: '04', label: 'Patio de Agua' }
  ],
  '4-01': [ // Heredia - Heredia
    { value: '01', label: 'Heredia' }, { value: '02', label: 'Mercedes' }, { value: '03', label: 'San Francisco' },
    { value: '04', label: 'Ulloa' }, { value: '05', label: 'Varablanca' }
  ],
  '4-02': [ // Heredia - Barva
    { value: '01', label: 'Barva' }, { value: '02', label: 'San Pedro' }, { value: '03', label: 'San Pablo' },
    { value: '04', label: 'San Roque' }, { value: '05', label: 'Santa Lucía' }, { value: '06', label: 'San José de la Montaña' }
  ],
  '4-03': [ // Heredia - Santo Domingo
    { value: '01', label: 'Santo Domingo' }, { value: '02', label: 'San Vicente' }, { value: '03', label: 'San Miguel' },
    { value: '04', label: 'Paracito' }, { value: '05', label: 'Santo Tomás' }, { value: '06', label: 'Santa Rosa' },
    { value: '07', label: 'Tures' }, { value: '08', label: 'Pará' }
  ],
  '4-04': [ // Heredia - Santa Bárbara
    { value: '01', label: 'Santa Bárbara' }, { value: '02', label: 'San Pedro' }, { value: '03', label: 'San Juan' },
    { value: '04', label: 'Jesús' }, { value: '05', label: 'Santo Domingo' }, { value: '06', label: 'Purabá' }
  ],
  '4-05': [ // Heredia - San Rafael
    { value: '01', label: 'San Rafael' }, { value: '02', label: 'San Josecito' }, { value: '03', label: 'Santiago' },
    { value: '04', label: 'Ángeles' }, { value: '05', label: 'Concepción' }
  ],
  '4-06': [ // Heredia - San Isidro
    { value: '01', label: 'San Isidro' }, { value: '02', label: 'San José' }, { value: '03', label: 'Concepción' },
    { value: '04', label: 'San Francisco' }
  ],
  '4-07': [ // Heredia - Belén
    { value: '01', label: 'San Antonio' }, { value: '02', label: 'La Ribera' }, { value: '03', label: 'La Asunción' }
  ],
  '4-08': [ // Heredia - Flores
    { value: '01', label: 'San Joaquín' }, { value: '02', label: 'Barrantes' }, { value: '03', label: 'Llorente' }
  ],
  '4-09': [ // Heredia - San Pablo
    { value: '01', label: 'San Pablo' }, { value: '02', label: 'Rincón de Sabanilla' }
  ],
  '4-10': [ // Heredia - Sarapiquí
    { value: '01', label: 'Puerto Viejo' }, { value: '02', label: 'La Virgen' }, { value: '03', label: 'Las Horquetas' },
    { value: '04', label: 'Llanuras del Gaspar' }, { value: '05', label: 'Cureña' }
  ],
  '5-01': [ // Guanacaste - Liberia
    { value: '01', label: 'Liberia' }, { value: '02', label: 'Cañas Dulces' }, { value: '03', label: 'Mayorga' },
    { value: '04', label: 'Nacascolo' }, { value: '05', label: 'Curubandé' }
  ],
  '5-02': [ // Guanacaste - Nicoya
    { value: '01', label: 'Nicoya' }, { value: '02', label: 'Mansión' }, { value: '03', label: 'San Antonio' },
    { value: '04', label: 'Quebrada Honda' }, { value: '05', label: 'Sámara' }, { value: '06', label: 'Nosara' },
    { value: '07', label: 'Belén de Nosarita' }
  ],
  '5-03': [ // Guanacaste - Santa Cruz
    { value: '01', label: 'Santa Cruz' }, { value: '02', label: 'Bolsón' }, { value: '03', label: 'Veintisiete de Abril' },
    { value: '04', label: 'Tempate' }, { value: '05', label: 'Cartagena' }, { value: '06', label: 'Cuajiniquil' },
    { value: '07', label: 'Diriá' }, { value: '08', label: 'Cabo Velas' }, { value: '09', label: 'Tamarindo' }
  ],
  '5-04': [ // Guanacaste - Bagaces
    { value: '01', label: 'Bagaces' }, { value: '02', label: 'La Fortuna' }, { value: '03', label: 'Mogote' },
    { value: '04', label: 'Río Naranjo' }
  ],
  '5-05': [ // Guanacaste - Carrillo
    { value: '01', label: 'Filadelfia' }, { value: '02', label: 'Palmira' }, { value: '03', label: 'Sardinal' },
    { value: '04', label: 'Belén' }
  ],
  '5-06': [ // Guanacaste - Cañas
    { value: '01', label: 'Cañas' }, { value: '02', label: 'Palmira' }, { value: '03', label: 'San Miguel' },
    { value: '04', label: 'Bebedero' }, { value: '05', label: 'Porozal' }
  ],
  '5-07': [ // Guanacaste - Abangares
    { value: '01', label: 'Las Juntas' }, { value: '02', label: 'Sierra' }, { value: '03', label: 'San Juan' },
    { value: '04', label: 'Colorado' }
  ],
  '5-08': [ // Guanacaste - Tilarán
    { value: '01', label: 'Tilarán' }, { value: '02', label: 'Quebrada Grande' }, { value: '03', label: 'Tronadora' },
    { value: '04', label: 'Santa Rosa' }, { value: '05', label: 'Líbano' }, { value: '06', label: 'Tierras Morenas' },
    { value: '07', label: 'Arenal' }
  ],
  '5-09': [ // Guanacaste - Nandayure
    { value: '01', label: 'Carmona' }, { value: '02', label: 'Santa Rita' }, { value: '03', label: 'Zapotal' },
    { value: '04', label: 'San Pablo' }, { value: '05', label: 'Porvenir' }, { value: '06', label: 'Bejuco' }
  ],
  '5-10': [ // Guanacaste - La Cruz
    { value: '01', label: 'La Cruz' }, { value: '02', label: 'Santa Cecilia' }, { value: '03', label: 'La Garita' },
    { value: '04', label: 'Santa Elena' }
  ],
  '5-11': [ // Guanacaste - Hojancha
    { value: '01', label: 'Hojancha' }, { value: '02', label: 'Monte Romo' }, { value: '03', label: 'Puerto Carrillo' },
    { value: '04', label: 'Huacas' }
  ],
  '6-01': [ // Puntarenas - Puntarenas
    { value: '01', label: 'Puntarenas' }, { value: '02', label: 'Pitahaya' }, { value: '03', label: 'Chomes' },
    { value: '04', label: 'Lepanto' }, { value: '05', label: 'Paquera' }, { value: '06', label: 'Manzanillo' },
    { value: '07', label: 'Guacimal' }, { value: '08', label: 'Barranca' }, { value: '09', label: 'Isla del Coco' },
    { value: '10', label: 'Cóbano' }, { value: '11', label: 'Chacarita' }, { value: '12', label: 'Chira' },
    { value: '13', label: 'Acapulco' }, { value: '14', label: 'El Roble' }, { value: '15', label: 'Arancibia' }
  ],
  '6-02': [ // Puntarenas - Esparza
    { value: '01', label: 'Espíritu Santo' }, { value: '02', label: 'San Juan Grande' }, { value: '03', label: 'Macacona' },
    { value: '04', label: 'San Rafael' }, { value: '05', label: 'San Jerónimo' }, { value: '06', label: 'Caldera' }
  ],
  '6-03': [ // Puntarenas - Buenos Aires
    { value: '01', label: 'Buenos Aires' }, { value: '02', label: 'Volcán' }, { value: '03', label: 'Potrero Grande' },
    { value: '04', label: 'Boruca' }, { value: '05', label: 'Pilas' }, { value: '06', label: 'Colinas' },
    { value: '07', label: 'Chánguena' }, { value: '08', label: 'Biolley' }, { value: '09', label: 'Brunka' }
  ],
  '6-04': [ // Puntarenas - Montes de Oro
    { value: '01', label: 'Miramar' }, { value: '02', label: 'La Unión' }, { value: '03', label: 'San Isidro' }
  ],
  '6-05': [ // Puntarenas - Osa
    { value: '01', label: 'Puerto Cortés' }, { value: '02', label: 'Palmar' }, { value: '03', label: 'Sierpe' },
    { value: '04', label: 'Bahía Ballena' }, { value: '05', label: 'Piedras Blancas' }, { value: '06', label: 'Bahía Drake' }
  ],
  '6-06': [ // Puntarenas - Quepos
    { value: '01', label: 'Quepos' }, { value: '02', label: 'Savegre' }, { value: '03', label: 'Naranjito' }
  ],
  '6-07': [ // Puntarenas - Golfito
    { value: '01', label: 'Golfito' }, { value: '02', label: 'Guaycará' }, { value: '03', label: 'Pavón' },
    { value: '04', label: 'Conte Burica' }
  ],
  '6-08': [ // Puntarenas - Coto Brus
    { value: '01', label: 'San Vito' }, { value: '02', label: 'Sabalito' }, { value: '03', label: 'Aguabuena' },
    { value: '04', label: 'Limoncito' }, { value: '05', label: 'Pittier' }, { value: '06', label: 'Gutiérrez Braun' }
  ],
  '6-09': [ // Puntarenas - Parrita
    { value: '01', label: 'Parrita' }
  ],
  '6-10': [ // Puntarenas - Corredores
    { value: '01', label: 'Corredor' }, { value: '02', label: 'La Cuesta' }, { value: '03', label: 'Canoas' },
    { value: '04', label: 'Laurel' }
  ],
  '6-11': [ // Puntarenas - Garabito
    { value: '01', label: 'Jacó' }, { value: '02', label: 'Tárcoles' }
  ],
  '6-12': [ // Puntarenas - Monte Verde
    { value: '01', label: 'Monte Verde' }
  ],
  '6-13': [ // Puntarenas - Puerto Jiménez
    { value: '01', label: 'Puerto Jiménez' }
  ],
  '7-01': [ // Limón - Limón
    { value: '01', label: 'Limón' }, { value: '02', label: 'Valle la Estrella' }, { value: '03', label: 'Río Blanco' },
    { value: '04', label: 'Matama' }
  ],
  '7-02': [ // Limón - Pococí
    { value: '01', label: 'Guápiles' }, { value: '02', label: 'Jiménez' }, { value: '03', label: 'La Rita' },
    { value: '04', label: 'Roxana' }, { value: '05', label: 'Cariari' }, { value: '06', label: 'Colorado' },
    { value: '07', label: 'La Colonia' }
  ],
  '7-03': [ // Limón - Siquirres
    { value: '01', label: 'Siquirres' }, { value: '02', label: 'Pacuarito' }, { value: '03', label: 'Florida' },
    { value: '04', label: 'Germania' }, { value: '05', label: 'El Cairo' }, { value: '06', label: 'Alegría' },
    { value: '07', label: 'Reventazón' }
  ],
  '7-04': [ // Limón - Talamanca
    { value: '01', label: 'Bratsi' }, { value: '02', label: 'Sixaola' }, { value: '03', label: 'Cahuita' },
    { value: '04', label: 'Telire' }
  ],
  '7-05': [ // Limón - Matina
    { value: '01', label: 'Matina' }, { value: '02', label: 'Batán' }, { value: '03', label: 'Carrandi' }
  ],
  '7-06': [ // Limón - Guácimo
    { value: '01', label: 'Guácimo' }, { value: '02', label: 'Mercedes' }, { value: '03', label: 'Pocora' },
    { value: '04', label: 'Río Jiménez' }, { value: '05', label: 'Duacarí' }
  ]
};

// Format identification based on type
const formatIdentificacion = (value, tipo) => {
  const digits = value.replace(/[^0-9]/g, '');

  switch (tipo) {
    case '01': // Física: X-XXXX-XXXX
      if (digits.length <= 1) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 1)}-${digits.slice(1)}`;
      return `${digits.slice(0, 1)}-${digits.slice(1, 5)}-${digits.slice(5, 9)}`;

    case '02': // Jurídica: X-XXX-XXXXXX
      if (digits.length <= 1) return digits;
      if (digits.length <= 4) return `${digits.slice(0, 1)}-${digits.slice(1)}`;
      return `${digits.slice(0, 1)}-${digits.slice(1, 4)}-${digits.slice(4, 10)}`;

    case '03': // DIMEX: sin formato
    case '04': // NITE: sin formato
    default:
      return digits;
  }
};

// Get clean digits from formatted identification
const getCleanIdentificacion = (value) => value.replace(/[^0-9]/g, '');

// Check if identification is complete based on type
const isIdentificacionComplete = (value, tipo) => {
  const digits = getCleanIdentificacion(value);
  const tipoInfo = TIPO_IDENTIFICACION.find(t => t.value === tipo);
  return tipoInfo ? digits.length === tipoInfo.length : false;
};

// Provisioning step descriptions with Lucide icons
const PROVISION_STEPS = {
  1: { label: 'Validando información', Icon: Search },
  2: { label: 'Verificando disponibilidad', Icon: ShieldCheck },
  3: { label: 'Creando cuenta', Icon: Building2 },
  4: { label: 'Creando base de datos', Icon: Database },
  5: { label: 'Creando tablas del sistema', Icon: Table2 },
  6: { label: 'Configurando parámetros', Icon: SlidersHorizontal },
  7: { label: 'Creando empresa emisora', Icon: Factory },
  8: { label: 'Creando usuario administrador', Icon: UserCog },
  9: { label: 'Vinculando permisos', Icon: KeyRound },
  10: { label: 'Inicializando consecutivos', Icon: Hash },
  11: { label: 'Finalizando configuración', Icon: Sparkles }
};

export default function Register() {
  const navigate = useNavigate();
  const { setAuthData } = useAuthStore();

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [lookingUpCedula, setLookingUpCedula] = useState(false);
  const [haciendaValidated, setHaciendaValidated] = useState(false);
  const [actividadesHacienda, setActividadesHacienda] = useState([]);

  // Provisioning state
  const [provisioning, setProvisioning] = useState(false);
  const [provisionStep, setProvisionStep] = useState(0);
  const [provisionMessage, setProvisionMessage] = useState('');
  const [provisionPercent, setProvisionPercent] = useState(0);
  const [provisionError, setProvisionError] = useState(null);
  const [provisionComplete, setProvisionComplete] = useState(false);
  const [provisionData, setProvisionData] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [displayedStep, setDisplayedStep] = useState(0);
  const eventSourceRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Tenant
    tenantName: '',
    // Step 2: User
    userName: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Step 3: Company
    tipoIdentificacion: '02',
    identificacion: '',
    nombreEmpresa: '',
    nombreComercial: '',
    emailEmpresa: '',
    telefono: '',
    provincia: '1',
    canton: '01',
    distrito: '01',
    direccion: '',
    actividadEconomica: '',
    actividadEconomicaDescripcion: ''
  });

  const [errors, setErrors] = useState({});

  // Generate slug from tenant name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };

  // Check slug availability when tenant name changes
  useEffect(() => {
    const checkSlugAvailability = async () => {
      if (formData.tenantName.length >= 3) {
        setCheckingSlug(true);
        try {
          const result = await onboardingApi.checkSlug(generateSlug(formData.tenantName));
          setSlugAvailable(result.available);
        } catch (error) {
          setSlugAvailable(null);
        }
        setCheckingSlug(false);
      } else {
        setSlugAvailable(null);
      }
    };

    const debounce = setTimeout(checkSlugAvailability, 500);
    return () => clearTimeout(debounce);
  }, [formData.tenantName]);

  // Cleanup event source on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Auto-lookup when identification is complete
  useEffect(() => {
    if (step === 3 && isIdentificacionComplete(formData.identificacion, formData.tipoIdentificacion) && !haciendaValidated && !lookingUpCedula) {
      lookupCedula();
    }
  }, [formData.identificacion, formData.tipoIdentificacion, step]);

  // Reset validation when tipo changes
  useEffect(() => {
    setHaciendaValidated(false);
    setActividadesHacienda([]);
    setFormData(prev => ({
      ...prev,
      identificacion: '',
      nombreEmpresa: '',
      actividadEconomica: '',
      actividadEconomicaDescripcion: ''
    }));
  }, [formData.tipoIdentificacion]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field changes
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle identificacion with auto-format
  const handleIdentificacionChange = (e) => {
    const rawValue = e.target.value;
    const formatted = formatIdentificacion(rawValue, formData.tipoIdentificacion);
    const tipoInfo = TIPO_IDENTIFICACION.find(t => t.value === formData.tipoIdentificacion);
    const maxDigits = tipoInfo?.length || 12;

    // Only update if within max length
    if (getCleanIdentificacion(formatted).length <= maxDigits) {
      setFormData(prev => ({ ...prev, identificacion: formatted }));
      setHaciendaValidated(false);

      if (errors.identificacion) {
        setErrors(prev => ({ ...prev, identificacion: null }));
      }
    }
  };

  // Handle canton change - reset distrito to first of new canton
  const handleCantonChange = (e) => {
    const newCanton = e.target.value;
    const distritos = DISTRITOS[`${formData.provincia}-${newCanton}`] || [];
    setFormData(prev => ({
      ...prev,
      canton: newCanton,
      distrito: distritos[0]?.value || '01'
    }));
  };

  // Handle provincia change - reset canton and distrito
  const handleProvinciaChange = (e) => {
    const newProvincia = e.target.value;
    const cantones = CANTONES[newProvincia] || [];
    const firstCanton = cantones[0]?.value || '01';
    const distritos = DISTRITOS[`${newProvincia}-${firstCanton}`] || [];
    setFormData(prev => ({
      ...prev,
      provincia: newProvincia,
      canton: firstCanton,
      distrito: distritos[0]?.value || '01'
    }));
  };

  const lookupCedula = async () => {
    if (!formData.identificacion) {
      return;
    }

    const cleanId = getCleanIdentificacion(formData.identificacion);
    const tipoInfo = TIPO_IDENTIFICACION.find(t => t.value === formData.tipoIdentificacion);

    if (cleanId.length !== tipoInfo?.length) {
      return;
    }

    setLookingUpCedula(true);
    try {
      const result = await onboardingApi.validateCedula(cleanId);
      if (result.success && result.data) {
        // Store all activities for selection
        const actividades = result.data.actividades || [];
        setActividadesHacienda(actividades);

        // Set first activity as default if available
        const primeraActividad = actividades.length > 0 ? actividades[0] : null;

        setFormData(prev => ({
          ...prev,
          nombreEmpresa: result.data.nombre || prev.nombreEmpresa,
          actividadEconomica: primeraActividad?.codigo || '',
          actividadEconomicaDescripcion: primeraActividad?.descripcion || '',
          provincia: result.data.provincia || prev.provincia,
          canton: result.data.canton || prev.canton,
          distrito: result.data.distrito || prev.distrito
        }));
        setHaciendaValidated(true);
        toast.success(`Datos cargados de Hacienda (${actividades.length} actividad${actividades.length !== 1 ? 'es' : ''})`);
      } else {
        toast.error(result.error || 'No se encontró información');
      }
    } catch (error) {
      toast.error('Error al consultar Hacienda');
    }
    setLookingUpCedula(false);
  };

  const validateStep = (stepNum) => {
    const newErrors = {};

    if (stepNum === 1) {
      if (!formData.tenantName || formData.tenantName.length < 3) {
        newErrors.tenantName = 'El nombre debe tener al menos 3 caracteres';
      }
      if (slugAvailable === false) {
        newErrors.tenantName = 'Este nombre ya está en uso';
      }
    }

    if (stepNum === 2) {
      if (!formData.userName || formData.userName.length < 2) {
        newErrors.userName = 'El nombre es requerido';
      }
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Email inválido';
      }
      if (!formData.password || formData.password.length < 8) {
        newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    }

    if (stepNum === 3) {
      if (!formData.identificacion) {
        newErrors.identificacion = 'La identificación es requerida';
      }
      if (!formData.nombreEmpresa || formData.nombreEmpresa.length < 3) {
        newErrors.nombreEmpresa = 'El nombre de la empresa es requerido';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  // Start provisioning with SSE
  const startProvisioning = async () => {
    if (!validateStep(3)) return;

    setStep(4); // Move to provisioning step
    setProvisioning(true);
    setProvisionError(null);
    setProvisionComplete(false);
    setProvisionStep(0);
    setDisplayedStep(0);
    setProvisionPercent(0);
    setProvisionMessage('Preparando...');
    setCompletedSteps([]);

    // Queue for animated step display
    const stepQueue = [];
    let isProcessingQueue = false;
    let completionData = null;

    // Process queue with animation delay - only show major step changes
    const processQueue = () => {
      if (stepQueue.length === 0) {
        isProcessingQueue = false;
        // If we have completion data and queue is empty, complete
        if (completionData) {
          finishProvisioning(completionData);
        }
        return;
      }

      isProcessingQueue = true;
      const nextStep = stepQueue.shift();

      // Only update display for step changes (not sub-messages within same step)
      setDisplayedStep(prev => {
        if (nextStep.step !== prev) {
          // Mark previous step as complete when moving to new step
          if (prev > 0) {
            setCompletedSteps(current => {
              if (!current.includes(prev)) {
                return [...current, prev];
              }
              return current;
            });
          }
          return nextStep.step;
        }
        return prev;
      });

      setProvisionMessage(nextStep.message);
      setProvisionPercent(nextStep.percent);

      // Longer delay for smoother visual effect (900ms between updates)
      setTimeout(processQueue, 900);
    };

    // Finish provisioning after queue is done
    const finishProvisioning = (data) => {
      // Mark all steps as complete
      setCompletedSteps([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]);

      setTimeout(() => {
        setProvisionComplete(true);
        setProvisionMessage(data.message);
        setProvisionPercent(100);
        setProvisionData(data.data);
        // Don't auto-redirect - let user click button
      }, 500);
    };

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/onboarding/provision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tenantName: formData.tenantName,
          email: formData.email,
          password: formData.password,
          userName: formData.userName,
          tipoIdentificacion: formData.tipoIdentificacion,
          identificacion: formData.identificacion,
          nombreEmpresa: formData.nombreEmpresa,
          nombreComercial: formData.nombreComercial,
          emailEmpresa: formData.emailEmpresa || formData.email,
          telefono: formData.telefono,
          provincia: formData.provincia,
          canton: formData.canton,
          distrito: formData.distrito,
          direccion: formData.direccion,
          actividadEconomica: formData.actividadEconomica
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'progress') {
                setProvisionStep(data.step);

                // Add to queue for animated display
                stepQueue.push({
                  step: data.step,
                  message: data.message,
                  percent: data.percent
                });

                // Start processing queue if not already
                if (!isProcessingQueue) {
                  processQueue();
                }
              } else if (data.type === 'complete') {
                // Store completion data
                completionData = data;

                // If queue is empty, finish now; otherwise it will finish after queue
                if (stepQueue.length === 0 && !isProcessingQueue) {
                  finishProvisioning(data);
                }
              } else if (data.type === 'error') {
                stepQueue.length = 0;
                setProvisionError(data.message);
                setProvisioning(false);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Provisioning error:', error);
      setProvisionError('Error de conexión. Por favor intente nuevamente.');
      setProvisioning(false);
    }
  };

  const retryProvisioning = () => {
    setProvisionError(null);
    setCompletedSteps([]);
    setDisplayedStep(0);
    setStep(3); // Go back to step 3
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[
        { num: 1, icon: Building2, label: 'Cuenta' },
        { num: 2, icon: User, label: 'Usuario' },
        { num: 3, icon: Settings, label: 'Empresa' },
        { num: 4, icon: Rocket, label: 'Crear' }
      ].map((s, idx) => (
        <div key={s.num} className="flex items-center">
          <div className={`flex flex-col items-center ${step >= s.num ? 'text-slate-800' : 'text-gray-400'}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
              step > s.num
                ? 'bg-green-500 border-green-500 text-white'
                : step === s.num
                  ? s.num === 4 && provisioning
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50'
                    : 'border-slate-800 text-slate-800'
                  : 'border-gray-300 text-gray-400'
            }`}>
              {step > s.num ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
            </div>
            <span className="text-xs mt-1 font-medium">{s.label}</span>
          </div>
          {idx < 3 && (
            <div className={`w-12 h-0.5 mx-2 ${step > s.num ? 'bg-green-500' : 'bg-gray-300'}`} />
          )}
        </div>
      ))}
    </div>
  );

  // Provisioning UI Component - Cinema credits style
  const ProvisioningUI = () => {
    const StepIcon = PROVISION_STEPS[displayedStep]?.Icon || Rocket;

    return (
      <div className="py-2">
        {provisionError ? (
          // Error state
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error al crear la cuenta</h3>
            <p className="text-gray-600 mb-8 max-w-sm mx-auto">{provisionError}</p>
            <button
              onClick={retryProvisioning}
              className="px-8 py-3 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-700 transition-all"
            >
              <ArrowLeft className="w-4 h-4 inline mr-2" />
              Volver a intentar
            </button>
          </div>
        ) : provisionComplete ? (
          // Complete state
          <div className="text-center py-6">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg animate-success-pop">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Cuenta creada!</h3>
            <p className="text-gray-600 mb-6">
              Tu cuenta <span className="font-semibold text-emerald-600">{formData.tenantName}</span> está lista.
            </p>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Empresa</p>
                    <p className="text-sm font-medium text-gray-900">{formData.nombreEmpresa}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Usuario</p>
                    <p className="text-sm font-medium text-gray-900">{formData.email}</p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setAuthData({
                  user: provisionData.user,
                  token: provisionData.token,
                  emisor: {
                    id: provisionData.emisor.id,
                    nombre: provisionData.emisor.nombre,
                    identificacion: provisionData.emisor.identificacion,
                    isDefault: true
                  },
                  tenantSlug: provisionData.tenant.slug
                });
                toast.success('¡Bienvenido a Finance 360!');

                // Redirect to tenant subdomain
                const hostname = window.location.hostname;
                const slug = provisionData.tenant.slug;

                if (hostname === 'localhost') {
                  // Development: stay on localhost
                  navigate('/dashboard');
                } else {
                  // Production: redirect to tenant subdomain
                  // Extract base domain (e.g., finance360.cr from app.finance360.cr)
                  const parts = hostname.split('.');
                  const baseDomain = parts.slice(-2).join('.');
                  window.location.href = `https://${slug}.${baseDomain}/dashboard`;
                }
              }}
              className="w-full py-3.5 bg-slate-800 text-white font-semibold rounded-xl hover:bg-slate-700 transition-all flex items-center justify-center gap-2"
            >
              Ir a Mi Cuenta
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        ) : (
          // Progress state - Cinema credits scrolling UP
          <div className="relative">
            {/* Header */}
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Configurando tu cuenta</h3>
              <p className="text-sm text-gray-500">{provisionPercent}% completado</p>
            </div>

            {/* Credits roll container */}
            <div className="relative h-72 overflow-hidden rounded-2xl bg-gradient-to-b from-slate-50 to-white">
              {/* Fade overlay top - where completed steps fade out */}
              <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-slate-50 via-slate-50/80 to-transparent z-10 pointer-events-none" />
              {/* Fade overlay bottom - where pending steps fade in */}
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white via-white/80 to-transparent z-10 pointer-events-none" />

              {/* Credits content - scrolls UP */}
              <div
                className="absolute left-0 right-0 px-4"
                style={{
                  top: '50%',
                  transform: `translateY(${-displayedStep * 64 + 32}px)`,
                  transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {Object.entries(PROVISION_STEPS).map(([num, step]) => {
                  const stepNum = parseInt(num);
                  const isComplete = completedSteps.includes(stepNum);
                  const isCurrent = stepNum === displayedStep;
                  const StepIconComponent = step.Icon;

                  // Calculate distance from current step for opacity
                  const distance = Math.abs(stepNum - displayedStep);
                  const opacity = isCurrent ? 1 : Math.max(0.15, 1 - distance * 0.25);

                  return (
                    <div
                      key={num}
                      className="h-16 flex items-center gap-4 transition-all duration-500"
                      style={{ opacity }}
                    >
                      {/* Icon */}
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                          isCurrent
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-500 shadow-md scale-110'
                            : isComplete
                            ? 'bg-emerald-100'
                            : 'bg-gray-100'
                        }`}
                      >
                        {isCurrent ? (
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        ) : isComplete ? (
                          <Check className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <StepIconComponent className="w-5 h-5 text-gray-400" />
                        )}
                      </div>

                      {/* Label */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-medium truncate transition-all duration-500 ${
                            isCurrent
                              ? 'text-gray-900'
                              : isComplete
                              ? 'text-gray-500'
                              : 'text-gray-400'
                          }`}
                        >
                          {step.label}
                        </p>
                        {isCurrent && provisionMessage && (
                          <p className="text-xs text-emerald-600 truncate">{provisionMessage}</p>
                        )}
                      </div>

                      {/* Checkmark for completed */}
                      {isComplete && (
                        <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {[...Array(11)].map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i + 1 <= displayedStep
                      ? 'w-3 bg-emerald-500'
                      : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* CSS for animations */}
        <style>{`
          @keyframes success-pop {
            0% { transform: scale(0.8); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-success-pop {
            animation: success-pop 0.5s ease-out forwards;
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Registration Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gray-50 py-12">
        <div className={`w-full transition-all duration-500 ${step === 3 && haciendaValidated ? 'max-w-4xl' : 'max-w-xl'}`}>
          {/* Card with gradient border */}
          <div
            className="rounded-3xl p-[3px]"
            style={{
              background: 'linear-gradient(135deg, #0D6E6E 0%, #7CB342 25%, #F5A623 50%, #E53935 75%, #1E88E5 100%)'
            }}
          >
            <div className="bg-white rounded-3xl p-8">
              {/* Header */}
              <div className="flex flex-col items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Finance 360</h1>
                <p className="text-[12px] font-medium self-center ml-24">
                  by <span className="shimmer-text">Kuanta Bridge</span>
                </p>
              </div>

              {/* Step Indicator */}
              <StepIndicator />

              <form onSubmit={(e) => e.preventDefault()}>
                {/* Step 1: Tenant Info */}
                {step === 1 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <h2 className="text-lg font-semibold text-gray-900">Crea tu cuenta</h2>
                      <p className="text-sm text-gray-500">Elige un nombre para identificar tu cuenta</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre de la cuenta
                      </label>
                      <input
                        type="text"
                        name="tenantName"
                        value={formData.tenantName}
                        onChange={handleChange}
                        placeholder="Ej: Mi Empresa"
                        className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none ${
                          errors.tenantName ? 'border-red-300' : 'border-gray-200'
                        }`}
                      />
                      {formData.tenantName && (
                        <div className="mt-1 flex items-center gap-2 text-sm">
                          <span className="text-gray-500">Tu URL será:</span>
                          <span className="font-mono text-slate-700">
                            {generateSlug(formData.tenantName)}.finance360.cr
                          </span>
                          {checkingSlug && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                          {!checkingSlug && slugAvailable === true && (
                            <span className="text-green-500 flex items-center gap-1">
                              <Check className="w-4 h-4" /> Disponible
                            </span>
                          )}
                          {!checkingSlug && slugAvailable === false && (
                            <span className="text-red-500">No disponible</span>
                          )}
                        </div>
                      )}
                      {errors.tenantName && (
                        <p className="mt-1 text-sm text-red-500">{errors.tenantName}</p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={!formData.tenantName || slugAvailable === false}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-slate-800 text-white font-medium rounded-xl hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-4"
                    >
                      Continuar
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Step 2: User Info */}
                {step === 2 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <h2 className="text-lg font-semibold text-gray-900">Tu información</h2>
                      <p className="text-sm text-gray-500">Crea tu usuario administrador</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                      <input
                        type="text"
                        name="userName"
                        value={formData.userName}
                        onChange={handleChange}
                        placeholder="Juan Pérez"
                        className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none ${
                          errors.userName ? 'border-red-300' : 'border-gray-200'
                        }`}
                      />
                      {errors.userName && <p className="mt-1 text-sm text-red-500">{errors.userName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="correo@ejemplo.com"
                        className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none ${
                          errors.email ? 'border-red-300' : 'border-gray-200'
                        }`}
                      />
                      {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Mínimo 8 caracteres"
                          className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none pr-12 ${
                            errors.password ? 'border-red-300' : 'border-gray-200'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Repita la contraseña"
                          className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none pr-12 ${
                            errors.confirmPassword ? 'border-red-300' : 'border-gray-200'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
                    </div>

                    <div className="flex gap-3 mt-4">
                      <button
                        type="button"
                        onClick={prevStep}
                        className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5" />
                        Atrás
                      </button>
                      <button
                        type="button"
                        onClick={nextStep}
                        className="flex-1 flex items-center justify-center gap-2 py-3 bg-slate-800 text-white font-medium rounded-xl hover:bg-slate-700 transition-colors"
                      >
                        Continuar
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Company Info */}
                {step === 3 && (
                  <div className="space-y-4">
                    <div className="text-center mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">Datos de la empresa</h2>
                      <p className="text-sm text-gray-500">
                        {haciendaValidated ? 'Completa la información del emisor' : 'Ingresa tu cédula para validar con Hacienda'}
                      </p>
                    </div>

                    {/* ANTES DE VALIDAR: Solo tipo e identificación */}
                    {!haciendaValidated && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo identificación</label>
                            <select
                              name="tipoIdentificacion"
                              value={formData.tipoIdentificacion}
                              onChange={(e) => setFormData(prev => ({ ...prev, tipoIdentificacion: e.target.value, identificacion: '' }))}
                              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none"
                            >
                              {TIPO_IDENTIFICACION.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Identificación</label>
                            <div className="relative">
                              <input
                                type="text"
                                name="identificacion"
                                value={formData.identificacion}
                                onChange={handleIdentificacionChange}
                                placeholder={TIPO_IDENTIFICACION.find(t => t.value === formData.tipoIdentificacion)?.placeholder || ''}
                                className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none pr-10 ${
                                  errors.identificacion ? 'border-red-300' : 'border-gray-200'
                                }`}
                              />
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                {lookingUpCedula && (
                                  <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                                )}
                              </div>
                            </div>
                            {errors.identificacion && <p className="mt-1 text-sm text-red-500">{errors.identificacion}</p>}
                            {lookingUpCedula && <p className="mt-1 text-sm text-emerald-600">Consultando Hacienda...</p>}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={prevStep}
                          className="w-full flex items-center justify-center gap-2 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors mt-4"
                        >
                          <ArrowLeft className="w-5 h-5" />
                          Atrás
                        </button>
                      </>
                    )}

                    {/* DESPUÉS DE VALIDAR: Todos los campos en layout ancho */}
                    {haciendaValidated && (
                      <>
                        {/* Row 1: Tipo, Identificación, Razón social */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                            <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
                              {TIPO_IDENTIFICACION.find(t => t.value === formData.tipoIdentificacion)?.label}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                              <span>Identificación</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setHaciendaValidated(false);
                                  setActividadesHacienda([]);
                                  setFormData(prev => ({
                                    ...prev,
                                    identificacion: '',
                                    nombreEmpresa: '',
                                    actividadEconomica: '',
                                    actividadEconomicaDescripcion: ''
                                  }));
                                }}
                                className="text-xs text-slate-500 hover:text-slate-700 hover:underline"
                              >
                                Cambiar
                              </button>
                            </label>
                            <div className="px-3 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 flex items-center gap-2">
                              <Check className="w-4 h-4" />
                              {formData.identificacion}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Razón social</label>
                            <input
                              type="text"
                              name="nombreEmpresa"
                              value={formData.nombreEmpresa}
                              onChange={handleChange}
                              placeholder="Nombre de la empresa"
                              className={`w-full px-3 py-2.5 border rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm ${
                                errors.nombreEmpresa ? 'border-red-300' : 'border-gray-200'
                              }`}
                            />
                          </div>
                        </div>

                        {/* Row 2: Nombre comercial, Email, Teléfono */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre comercial</label>
                            <input
                              type="text"
                              name="nombreComercial"
                              value={formData.nombreComercial}
                              onChange={handleChange}
                              placeholder="Opcional"
                              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email facturación</label>
                            <input
                              type="email"
                              name="emailEmpresa"
                              value={formData.emailEmpresa}
                              onChange={handleChange}
                              placeholder={formData.email || 'correo@empresa.com'}
                              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                            <input
                              type="tel"
                              name="telefono"
                              value={formData.telefono}
                              onChange={handleChange}
                              placeholder="88887777"
                              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm"
                            />
                          </div>
                        </div>

                        {/* Actividad Económica */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Actividad Económica {actividadesHacienda.length > 1 && <span className="text-gray-400 font-normal">({actividadesHacienda.length} disponibles)</span>}
                          </label>
                          {actividadesHacienda.length > 1 ? (
                            <select
                              value={formData.actividadEconomica}
                              onChange={(e) => {
                                const selected = actividadesHacienda.find(a => a.codigo === e.target.value);
                                setFormData(prev => ({
                                  ...prev,
                                  actividadEconomica: selected?.codigo || '',
                                  actividadEconomicaDescripcion: selected?.descripcion || ''
                                }));
                              }}
                              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm"
                            >
                              {actividadesHacienda.map((act, idx) => (
                                <option key={act.codigo} value={act.codigo}>
                                  {act.codigo} - {act.descripcion}
                                </option>
                              ))}
                            </select>
                          ) : actividadesHacienda.length === 1 ? (
                            <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                              {formData.actividadEconomica} - {formData.actividadEconomicaDescripcion}
                            </div>
                          ) : (
                            <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                              No registrada en Hacienda
                            </div>
                          )}
                        </div>

                        {/* Row 3: Ubicación */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                            <select
                              name="provincia"
                              value={formData.provincia}
                              onChange={handleProvinciaChange}
                              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm"
                            >
                              {PROVINCIAS.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cantón</label>
                            <select
                              name="canton"
                              value={formData.canton}
                              onChange={handleCantonChange}
                              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm"
                            >
                              {(CANTONES[formData.provincia] || []).map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Distrito</label>
                            <select
                              name="distrito"
                              value={formData.distrito}
                              onChange={handleChange}
                              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm"
                            >
                              {(DISTRITOS[`${formData.provincia}-${formData.canton}`] || []).map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Otras señas */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Otras señas (opcional)</label>
                          <input
                            type="text"
                            name="direccion"
                            value={formData.direccion}
                            onChange={handleChange}
                            placeholder="100m norte del parque central"
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all outline-none text-sm"
                          />
                        </div>

                        {/* Botones */}
                        <div className="flex gap-3 mt-4">
                          <button
                            type="button"
                            onClick={prevStep}
                            className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            <ArrowLeft className="w-5 h-5" />
                            Atrás
                          </button>
                          <button
                            type="button"
                            onClick={startProvisioning}
                            className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-xl transition-colors shadow-lg shadow-emerald-500/25"
                          >
                            <Rocket className="w-5 h-5" />
                            Crear cuenta
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Step 4: Provisioning */}
                {step === 4 && <ProvisioningUI />}

                {/* Login link */}
                {step < 4 && (
                  <p className="text-center text-sm text-gray-500 mt-6">
                    ¿Ya tienes cuenta?{' '}
                    <Link to="/login" className="text-slate-700 font-medium hover:underline">
                      Iniciar sesión
                    </Link>
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-slate-800 to-slate-900 items-center justify-center p-12">
        <div className="max-w-md text-center">
          {/* Logo icon */}
          <div className="mb-8">
            <div
              className="inline-flex rounded-2xl p-[3px]"
              style={{
                background: 'linear-gradient(135deg, #0D6E6E 0%, #7CB342 25%, #F5A623 50%, #E53935 75%, #1E88E5 100%)'
              }}
            >
              <div className="bg-slate-800 rounded-2xl p-4">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white mb-4">
            Empieza a facturar hoy
          </h2>
          <p className="text-gray-300 text-lg mb-12">
            Crea tu cuenta en minutos y comienza a emitir facturas electrónicas de forma fácil y rápida.
          </p>

          {/* Features */}
          <div className="space-y-4 text-left">
            {[
              'Integración directa con TribuCR / Hacienda',
              'Firma digital automática',
              'Soporte para todos los tipos de documento',
              'Sin límite de facturas'
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
